import { workspacePath } from "../context/WorkspaceContext";
import type { TabId } from "../pages/EventOS/types";
import type { WorkspaceId } from "../utils/workspaceStorage";
import { appNav } from "./appNavigation";

const LAST_EVENT_KEY_PREFIX = "zoniq_last_event_";

/**
 * Router `location.state` shape for "return to where you came from" links
 * inside the event workspace (e.g. the "← Events" back link). Set this via
 * `navigate(path, { state })` when deep-linking into a specific event tab
 * from somewhere other than the plain Events list, so the back link returns
 * the user to that origin instead of the default list.
 */
export type EventWorkspaceBackState = {
  backTo: string;
  backLabel: string;
};

export function readLastEventId(workspace: WorkspaceId): string | null {
  if (typeof window === "undefined") return null;
  const id = sessionStorage.getItem(`${LAST_EVENT_KEY_PREFIX}${workspace}`);
  return id?.trim() || null;
}

export function writeLastEventId(workspace: WorkspaceId, eventId: string): void {
  if (typeof window === "undefined") return;
  const trimmed = (eventId || "").trim();
  if (!trimmed) return;
  sessionStorage.setItem(`${LAST_EVENT_KEY_PREFIX}${workspace}`, trimmed);
}

export function clearLastEventId(workspace: WorkspaceId): void {
  if (typeof window === "undefined") return;
  sessionStorage.removeItem(`${LAST_EVENT_KEY_PREFIX}${workspace}`);
}

export function parseEventIdFromPath(pathname: string, _workspace?: WorkspaceId): string | null {
  const match = pathname.match(/\/events\/([^/]+)\//);
  return match?.[1] ?? null;
}

/** Unified flat navigation (Section 4.3). Workspace param kept for query-key compatibility. */
export const workspaceNav = {
  home: (_workspace: WorkspaceId) => appNav.home(),
  calendar: (_workspace: WorkspaceId) => appNav.calendar(),
  events: (_workspace: WorkspaceId) => appNav.events(),
  analytics: (_workspace: WorkspaceId) => appNav.analytics(),
  newEvent: (_workspace: WorkspaceId, date?: string) => appNav.newEvent(date),
  editEvent: (_workspace: WorkspaceId, eventId: string) => appNav.editEvent(eventId),
  artists: (_workspace: WorkspaceId) => appNav.artists(),
  contacts: (_workspace: WorkspaceId) => appNav.contacts(),
  venues: (_workspace: WorkspaceId) => appNav.venues(),
  profile: (_workspace: WorkspaceId) => appNav.profile(),
  commandCenterEntry: (_workspace: WorkspaceId) => appNav.events(),
  dashboardEntry: (_workspace: WorkspaceId) => appNav.events(),
  commandCenter: (_workspace: WorkspaceId, eventId: string, tab: TabId = "overview") =>
    appNav.commandCenter(eventId, tab),
  dashboard: (_workspace: WorkspaceId, eventId: string) => appNav.eventOverview(eventId),
  logistics: (_workspace: WorkspaceId, eventId: string) => appNav.eventBudget(eventId),
  invoices: (_workspace: WorkspaceId, eventId: string) => appNav.eventInvoices(eventId),
  eventOverview: (_workspace: WorkspaceId, eventId: string) => appNav.eventOverview(eventId),
  eventTickets: (_workspace: WorkspaceId, eventId: string) => appNav.eventTickets(eventId),
  eventBudget: (_workspace: WorkspaceId, eventId: string) => appNav.eventBudget(eventId),
  eventPnl: (_workspace: WorkspaceId, eventId: string) => appNav.eventPnl(eventId),
  eventRunOfShow: (_workspace: WorkspaceId, eventId: string) => appNav.eventRunOfShow(eventId),
  eventLive: (_workspace: WorkspaceId, eventId: string) => appNav.eventLive(eventId),
  eventInvoices: (_workspace: WorkspaceId, eventId: string) => appNav.eventInvoices(eventId),
  eventContacts: (_workspace: WorkspaceId, eventId: string) => appNav.eventContacts(eventId),
};

export function commandCenterPath(workspace: WorkspaceId, eventId: string | null): string {
  return eventId ? workspaceNav.commandCenter(workspace, eventId) : workspaceNav.events(workspace);
}

export function dashboardPathForEvent(workspace: WorkspaceId, eventId: string | null): string {
  return eventId ? workspaceNav.dashboard(workspace, eventId) : workspaceNav.events(workspace);
}

export function resolveActiveEventId(pathname: string, workspace: WorkspaceId): string | null {
  return parseEventIdFromPath(pathname, workspace) ?? readLastEventId(workspace);
}

export { workspacePath };
